import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useState } from "react";

const SECTIONS = [
  { h: "The Florida 2+2 promise", b: "Earn an AA from a Florida public state college and the state guarantees admission to one of 12 public state universities. 60 credits articulate. The promise is real — and partial." },
  { h: "What the 'guarantee' actually means", b: "It guarantees a state university, not your first choice. It does not guarantee admission to a limited-access major. Most students discover this distinction one semester too late.", pull: "The guarantee is the floor of the staircase. Not the door at the top." },
  { h: "The 24 elective credits are not filler", b: "They are strategic prerequisite real estate. The single biggest source of wasted time at Florida community colleges is treating electives as fillers instead of as the runway to a specific limited-access major." },
  { h: "The Common Prerequisites Manual", b: "The source of truth for major-specific requirements. Every recommendation we make starts here. Every wasted semester begins with not reading it." },
  { h: "FloridaShines", b: "What it is, what it isn't, how to actually use it. FloridaShines is a state-run information layer; it is not an admissions strategist and it does not enforce the CPM." },
  { h: "AA vs. AS", b: "The difference, when each makes sense. The AA is the universal transfer credential. The AS articulates narrowly. Choose wrong and you spend the next two years buying credits you can't use." },
  { h: "The four most common mistakes", b: "(1) The 'guarantee' misconception. (2) Random electives. (3) Late awareness of prerequisites. (4) Poor application timing." },
];

const STATE_MILESTONES = [
  "First semester · Lock the major thesis and the four core classes.",
  "First semester · Anchor with 3 professors.",
  "Second semester · Confirm AA pathway against the CPM.",
  "Second semester · Activate FUSE / DirectConnect / Aim Higher.",
  "Summer · First out-of-class proof point.",
  "Third semester · Build the school list (3-2-1).",
  "Third semester · Visit campuses.",
  "Third semester · Draft the personal statement.",
  "Fourth semester (Fall) · Open and submit early.",
  "Fourth semester (Winter) · Track recommender follow-ups.",
  "Fourth semester (Spring) · Decision letters.",
  "After AA · Onboard at the new school.",
];

const SELECTIVE_MILESTONES = [
  "First semester · Define the academic-necessity thesis.",
  "First semester · Anchor with 3 faculty including 1 in target discipline.",
  "Second semester · Choose two narrative projects.",
  "Second semester · Audit the activities slate.",
  "Summer · Major spike #1 (research or publication).",
  "Third semester · Target-school research (3 reasons per school).",
  "Third semester · Recommender briefings.",
  "Third semester · Personal statement architecture.",
  "Third semester · Supplementals.",
  "Fourth semester (Winter) · Submit.",
  "Fourth semester (Spring) · Interview prep.",
  "After admit · Yield decision + transition.",
];

export default function FloridaTransferSystem() {
  const [tab, setTab] = useState("state");
  return (
    <Layout>
      <SEO
        title="How the Florida 2+2 Transfer System Actually Works"
        description="Long-form explainer of the Florida 2+2 / FloridaShines system. What the guarantee means, what it doesn't, and where the strategic levers are for Valencia, Seminole, Miami-Dade transfers."
        canonical="/florida-transfer-system"
        keywords="Florida 2+2, FloridaShines, Common Prerequisites Manual, AA transfer, Valencia transfer, Florida community college guarantee"
      />
      <section className="border-b border-stone/30 bg-bone" data-testid="florida-system-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20">
          <Eyebrow>The Florida 2+2 System · Placeholder</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.75rem] text-ink leading-[1.04] tracking-tightest mt-5">
            How <span className="italic text-ivy">Florida transfer</span> actually works.
          </h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-3xl leading-relaxed">
            A long-form explainer of the Florida 2+2 / FloridaShines system — what it guarantees, what it doesn&rsquo;t, and where the strategic levers are.
          </p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="space-y-12">
          {SECTIONS.map((s, i) => (
            <div key={s.h} className="border-l-2 border-brass pl-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brass">{String(i + 1).padStart(2, "0")}</div>
              <h2 className="font-serif font-medium text-2xl sm:text-3xl text-ink mt-2 tracking-tight">{s.h}</h2>
              <p className="mt-4 text-ink-soft leading-relaxed">{s.b}</p>
              {s.pull && <p className="mt-5 font-serif italic text-xl text-brass leading-snug">&ldquo;{s.pull}&rdquo;</p>}
            </div>
          ))}
        </div>
      </article>

      <section className="bg-bone-dim border-y border-stone/30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="florida-timeline">
          <SectionLabel index="•" label="The Transfer Timeline" />
          <h2 className="font-serif font-medium text-3xl sm:text-4xl text-ink mt-4 leading-tight tracking-tight">Month by month, two tracks.</h2>
          <Tabs value={tab} onValueChange={setTab} className="mt-10">
            <TabsList className="bg-bone border border-stone/40 w-full sm:w-auto rounded-none p-0 h-auto">
              <TabsTrigger value="state" data-testid="tab-state" className="rounded-none data-[state=active]:bg-ivy data-[state=active]:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold px-6 py-3">State Transfer Track</TabsTrigger>
              <TabsTrigger value="selective" data-testid="tab-selective" className="rounded-none data-[state=active]:bg-ivy data-[state=active]:text-bone uppercase tracking-[0.18em] text-[11px] font-semibold px-6 py-3">Selective / Nationwide</TabsTrigger>
            </TabsList>
            <TabsContent value="state" className="mt-10">
              <ol className="relative pl-10 border-l border-ivy/30 space-y-7">
                {STATE_MILESTONES.map((m, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[42px] top-1 font-serif text-brass text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-ink leading-relaxed">{m}</span>
                  </li>
                ))}
              </ol>
            </TabsContent>
            <TabsContent value="selective" className="mt-10">
              <ol className="relative pl-10 border-l border-ivy/30 space-y-7">
                {SELECTIVE_MILESTONES.map((m, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[42px] top-1 font-serif text-brass text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-ink leading-relaxed">{m}</span>
                  </li>
                ))}
              </ol>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
        <a href="/transfer-audit" className="inline-block bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors">Get an audit of where you stand</a>
      </section>
    </Layout>
  );
}
